import { useState, useCallback } from 'react'
import { Head, Link } from '@inertiajs/react'
import { Ghost, Clock, Search, MessageCircle } from 'lucide-react'
import MainLayout from '@/components/Layout/MainLayout'
import { SidebarInput } from '@/components/ui/shadcn/sidebar'
import { ConversationFilter } from '@/components/ConversationFilter'
import { FolderSelector } from '@/components/FolderSelector'
import { FilterProvider, useFilter, type FilterOption, type FolderOption, type AssignmentOption } from '@/contexts/FilterContext'
import { useInboxChannel } from '@/hooks/useCable'
import { useStaleRefresh } from '@/hooks/useStaleRefresh'
import { usePaginatedConversations } from '@/hooks/usePaginatedConversations'
import { LoadMoreConversations } from '@/components/inbox/LoadMoreConversations'
import { useRouteParams } from '@/hooks/useRoutes'
import routes from '@/routeHelpers'
import { cn } from '@/lib/utils'
import { formatTimeAgo } from '@/lib/dateUtils'
import type { InboxMessage } from '@/lib/cable'
import { mergeConversationUpdate, filterConversationsForView } from '@/lib/conversationListUpdate'
import { VisitorAvatar } from '@/components/ui/VisitorAvatar'
import { WidgetSetupPrompt, useWidgetConfigured } from '@/components/WidgetSetupPrompt'
import type { CurrentUser, Account } from '@/types/shared'

interface Visitor {
  id: string
  name: string
  email: string | null
  identified: boolean
  active?: boolean
  last_seen_at?: string | null
  country_code?: string | null
}

interface LastMessage {
  content: string
  sender_type: 'Visitor' | 'User'
  created_at: string
}

interface Conversation {
  id: string
  inbox_id: string
  status: 'open' | 'closed'
  subject?: string | null
  display_subject?: string | null
  unread?: boolean
  visitor: Visitor
  last_message: LastMessage | null
  messages_count: number
  last_message_at: string | null
  created_at: string
}

interface Inbox {
  id: string
  slug: string
  name: string
}

interface InboxProps {
  current_user: CurrentUser
  inbox: Inbox | null  // null when viewing all inboxes
  conversations: Conversation[]
  conversations_next_cursor: string | null
  current_account: Account | null
  current_filter?: FilterOption
  current_folder?: FolderOption
  current_assignment?: AssignmentOption
}

const emptyStateQuips = [
  "In the empty inbox, no one can hear you scream.",
  "It's quiet... too quiet.",
  "The ghosts are resting. For now.",
  "Nothing to haunt you here. Yet.",
  "Tumbleweeds and silence.",
  "Your inbox is so empty it echoes.",
  "The void stares back. Politely.",
  "Peace and quiet. Suspiciously so.",
  "Not a peep. Not a boo.",
  "Even the ghosts took a day off.",
]

function getRandomQuip(): string {
  return emptyStateQuips[Math.floor(Math.random() * emptyStateQuips.length)]
}

function InboxContent({ current_user, inbox, conversations: initialConversations, conversations_next_cursor, current_account }: Omit<InboxProps, 'current_filter' | 'current_folder' | 'current_assignment'>) {
  const { withAccount, withInbox } = useRouteParams()
  const { currentFilter, currentFolder, currentAssignment } = useFilter()
  const { conversations, setConversations, nextCursor, appendPage } = usePaginatedConversations(initialConversations, conversations_next_cursor)
  const [newConversationIds, setNewConversationIds] = useState<Set<string>>(new Set())
  const [searchQuery, setSearchQuery] = useState("")
  const [emptyQuip] = useState(getRandomQuip)
  const widgetConfigured = useWidgetConfigured()

  // Cable updates mutate `conversations` in place, so the server-side filter
  // can go stale (e.g. a conversation just resolved while "Unresolved" is
  // selected). Re-apply the current filter/folder locally to keep the list honest.
  const visibleConversations = filterConversationsForView(conversations, currentFilter, currentFolder, currentAssignment, current_user.id)
  const filteredConversations = searchQuery
    ? visibleConversations.filter(c =>
        c.visitor.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        c.visitor.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        c.display_subject?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        c.last_message?.content.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : visibleConversations

  const hasConversations = visibleConversations.length > 0

  // Handle incoming cable messages (sound is handled by NotificationsContext)
  const handleCableMessage = useCallback((data: InboxMessage) => {
    if (data.type === 'new_conversation') {
      setConversations(prev => {
        // Don't add if already exists
        if (prev.some(c => c.id === data.conversation.id)) return prev
        return [{ ...data.conversation, unread: true }, ...prev]
      })
      // Mark as new for glow effect
      setNewConversationIds(prev => new Set(prev).add(data.conversation.id))
      // Remove glow after 3 seconds
      setTimeout(() => {
        setNewConversationIds(prev => {
          const next = new Set(prev)
          next.delete(data.conversation.id)
          return next
        })
      }, 3000)
    } else if (data.type === 'conversation_updated') {
      setConversations(prev => {
        const index = prev.findIndex(c => c.id === data.conversation.id)
        if (index === -1) return prev
        const updated = [...prev]
        updated[index] = mergeConversationUpdate(prev[index], data.conversation)
        // Move to top if has new message
        updated.sort((a, b) => {
          const aTime = a.last_message_at || a.created_at
          const bTime = b.last_message_at || b.created_at
          return new Date(bTime).getTime() - new Date(aTime).getTime()
        })
        return updated
      })
      // Mark as updated for glow effect
      setNewConversationIds(prev => new Set(prev).add(data.conversation.id))
      setTimeout(() => {
        setNewConversationIds(prev => {
          const next = new Set(prev)
          next.delete(data.conversation.id)
          return next
        })
      }, 3000)
    }
  }, [setConversations])

  // Subscribe to inbox channel (NotificationsProvider handles sounds/title)
  useInboxChannel(current_account?.slug, handleCableMessage)

  // Refresh data when returning to tab after 5+ minutes
  useStaleRefresh()

  return (
    <>
      <Head title={inbox ? `${inbox.name} - Inbox` : 'Inbox'} />
      <MainLayout
        user={current_user}
        breadcrumbs={inbox ? [{ label: 'Inbox', href: routes.inbox.show.path(withAccount()) }, { label: inbox.name }] : [{ label: 'Inbox' }]}
      >
        <div className="flex flex-1 min-h-0">
          {/* Second sidebar - Conversations list (same styling as original) */}
          <div className="hidden md:flex w-80 flex-col min-h-0 border-r border-sidebar-border bg-sidebar-secondary text-sidebar-secondary-foreground">
            <div className="gap-3.5 border-b border-sidebar-secondary-accent/30 p-4">
              <div className="flex w-full items-center justify-between mb-3">
                <div className="flex items-center gap-1">
                  <FolderSelector />
                  <ConversationFilter />
                </div>
                <div className="text-xs text-sidebar-secondary-muted">
                  {visibleConversations.filter(c => c.status === 'open').length} open
                </div>
              </div>
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 size-4 text-sidebar-secondary-muted" />
                <SidebarInput
                  placeholder="Search conversations..."
                  className="pl-8"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto">
              {filteredConversations.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 text-center px-4">
                  <div className="size-12 rounded-full bg-primary/20 flex items-center justify-center mb-3">
                    <Ghost className="size-6 text-primary" />
                  </div>
                  <p className="text-sm text-sidebar-secondary-muted">
                    {searchQuery
                      ? "No conversations found"
                      : conversations.length > 0
                        ? "No conversations match this filter"
                        : "No conversations yet"}
                  </p>
                </div>
              ) : (
                filteredConversations.map((conversation) => (
                  <Link
                    href={`${routes.conversations.show.path(withInbox({ id: conversation.id, inbox_id: conversation.inbox_id }))}?filter=${currentFilter}&folder=${currentFolder}&assignment=${currentAssignment}`}
                    key={conversation.id}
                    className={cn(
                      "flex flex-col items-start gap-2 border-b border-sidebar-secondary-accent/30 p-4 text-sm leading-tight whitespace-nowrap last:border-b-0 cursor-pointer",
                      "hover:bg-sidebar-secondary-accent/50",
                      conversation.status === 'closed' && "opacity-60",
                      newConversationIds.has(conversation.id) && "ghost-glow"
                    )}
                  >
                    <div className="flex w-full items-center gap-2">
                      <VisitorAvatar
                        name={conversation.visitor.name}
                        identified={conversation.visitor.identified}
                        active={conversation.visitor.active}
                        countryCode={conversation.visitor.country_code}
                      />
                      <span className="truncate font-medium text-sidebar-secondary-foreground">{conversation.visitor.name}</span>
                      {conversation.visitor.last_seen_at && (
                        <span className="ml-auto text-xs text-sidebar-secondary-muted flex items-center gap-1" title="Last seen">
                          <Clock className="size-3" />
                          {formatTimeAgo(conversation.visitor.last_seen_at)}
                        </span>
                      )}
                    </div>
                    {conversation.display_subject && (
                      <span className="truncate w-full text-xs font-medium text-sidebar-secondary-foreground">
                        {conversation.display_subject}
                      </span>
                    )}
                    {conversation.last_message && (
                      <span className="line-clamp-2 w-full text-xs whitespace-break-spaces text-sidebar-secondary-muted">
                        {conversation.last_message.sender_type === 'User' && (
                          <span className="text-primary">You: </span>
                        )}
                        {conversation.last_message.content}
                      </span>
                    )}
                    <div className="flex items-center gap-2">
                      {conversation.unread && (
                        <div className="size-2 rounded-full bg-blue-500" title="Unread" />
                      )}
                      {!conversation.unread && conversation.status === 'open' && (
                        <div className="size-1.5 rounded-full bg-primary" title="Open" />
                      )}
                      {conversation.visitor.email && (
                        <span className="text-xs text-sidebar-secondary-muted truncate">
                          {conversation.visitor.email}
                        </span>
                      )}
                    </div>
                  </Link>
                ))
              )}
              {!searchQuery && <LoadMoreConversations cursor={nextCursor} onLoaded={appendPage} />}
            </div>
          </div>

          {/* Main content area */}
          <div className="flex flex-1 flex-col items-center justify-center p-8 text-center">
            {hasConversations ? (
              <>
                <div className="size-16 rounded-full bg-primary/20 flex items-center justify-center mb-6">
                  <MessageCircle className="size-8 text-primary" />
                </div>
                <h3 className="text-lg font-medium mb-2">Select a conversation</h3>
                <p className="text-muted-foreground max-w-md">
                  Choose a conversation from the list to view messages and reply.
                </p>
              </>
            ) : !widgetConfigured ? (
              <WidgetSetupPrompt />
            ) : conversations.length > 0 ? (
              <>
                <div className="size-20 rounded-full bg-primary/20 flex items-center justify-center mb-6">
                  <Ghost className="size-10 text-primary" />
                </div>
                <h3 className="text-lg font-medium mb-2">No conversations match this filter</h3>
                <p className="text-muted-foreground max-w-md">
                  Try a different folder or filter.
                </p>
              </>
            ) : (
              <>
                <div className="size-20 rounded-full bg-primary/20 flex items-center justify-center mb-6">
                  <Ghost className="size-10 text-primary" />
                </div>
                <h3 className="text-lg font-medium mb-2">No conversations yet</h3>
                <p className="text-muted-foreground max-w-md italic">
                  {emptyQuip}
                </p>
              </>
            )}
          </div>
        </div>
      </MainLayout>
    </>
  )
}

export default function Inbox({ current_user, inbox, conversations, conversations_next_cursor, current_account, current_filter = 'unresolved', current_folder = 'inbox', current_assignment = 'everyone' }: InboxProps) {
  return (
    <FilterProvider currentFilter={current_filter} currentFolder={current_folder} currentAssignment={current_assignment}>
      <InboxContent
        current_user={current_user}
        inbox={inbox}
        conversations={conversations}
        conversations_next_cursor={conversations_next_cursor}
        current_account={current_account}
      />
    </FilterProvider>
  )
}
